(() => {
  class CromaCarousel extends HTMLElement {
    constructor() {
      super();
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onScroll = this.updateControls.bind(this);
      this.onResize = this.updateControls.bind(this);
      this.onPrevClick = () => this.scrollPage(-1);
      this.onNextClick = () => this.scrollPage(1);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-croma-carousel-track]');
      this.prevButton = this.querySelector('[data-croma-carousel-prev]');
      this.nextButton = this.querySelector('[data-croma-carousel-next]');
      this.currentTarget = this.querySelector('[data-croma-carousel-current]');
      this.totalTarget = this.querySelector('[data-croma-carousel-total]');
      this.statusTarget = this.querySelector('[data-croma-carousel-status]');
      this.items = Array.from(this.querySelectorAll('[data-croma-carousel-item]'));

      if (this.dataset.cromaCarouselLayout !== 'carousel' || !this.track) {
        return;
      }

      this.prevButton?.addEventListener('click', this.onPrevClick);
      this.nextButton?.addEventListener('click', this.onNextClick);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.updateControls();
      requestAnimationFrame(() => this.updateControls());
    }

    disconnectedCallback() {
      this.prevButton?.removeEventListener('click', this.onPrevClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.track?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    scrollPage(direction) {
      if (!this.track) {
        return;
      }

      this.track.scrollBy({
        left: this.getPageDistance() * direction,
        behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth',
      });
    }

    getPageDistance() {
      const item = this.track?.querySelector('[data-croma-carousel-item]');

      if (!this.track || !item) {
        return 0;
      }

      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0');
      const padding = parseFloat(styles.paddingLeft || '0') + parseFloat(styles.paddingRight || '0');
      const itemWidth = item.getBoundingClientRect().width;
      const visibleWidth = this.track.clientWidth - padding;
      const visibleItems = Math.max(1, Math.floor((visibleWidth + gap) / (itemWidth + gap)));

      return visibleItems * (itemWidth + gap);
    }

    updateControls() {
      if (!this.track || !this.prevButton || !this.nextButton) {
        return;
      }

      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const currentScroll = this.track.scrollLeft;
      const threshold = 2;

      this.prevButton.disabled = currentScroll <= threshold;
      this.nextButton.disabled = currentScroll >= maxScroll - threshold;
      this.classList.toggle('croma-carousel--no-controls', maxScroll <= threshold);
      this.updatePosition();
    }

    updatePosition() {
      if (!this.track || !this.items.length || (!this.currentTarget && !this.statusTarget)) {
        return;
      }

      const currentIndex = this.items.reduce((closestIndex, item, index) => {
        const currentDistance = Math.abs(this.items[closestIndex].offsetLeft - this.track.scrollLeft);
        const itemDistance = Math.abs(item.offsetLeft - this.track.scrollLeft);

        return itemDistance < currentDistance ? index : closestIndex;
      }, 0);
      const current = currentIndex + 1;
      const total = this.items.length;

      if (this.currentTarget) {
        this.currentTarget.textContent = current;
      }

      if (this.totalTarget) {
        this.totalTarget.textContent = total;
      }

      if (this.statusTarget) {
        const template = this.statusTarget.dataset.cromaCarouselStatusTemplate || '[current] / [total]';
        this.statusTarget.textContent = template
          .replace('[current]', current)
          .replace('[total]', total);
      }
    }
  }

  ['croma-featured-products', 'croma-featured-collections', 'croma-product-gallery'].forEach((elementName) => {
    if (!customElements.get(elementName)) {
      customElements.define(elementName, class extends CromaCarousel {});
    }
  });
})();
